import React from "react";
import { motion } from "framer-motion";
import {
  NewspaperIcon,
  CalendarIcon,
  UserIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";

const PressRelease = () => {
  return (
    <div className="bg-gradient-to-br from-dark-800 to-dark-900 py-16 sm:py-24">
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      <div className="relative mx-auto max-w-4xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="p-3 rounded-full bg-primary-500/20">
              <NewspaperIcon className="h-8 w-8 text-primary-400" />
            </div>
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Press Release
          </h1>
          <p className="mt-4 text-lg leading-8 text-primary-200">
            Latest news and updates from SpeedR AI
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-8 sm:p-12 border border-primary-900/20"
        >
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 text-primary-300 text-sm mb-4">
              <div className="flex items-center">
                <CalendarIcon className="h-4 w-4 mr-1" />
                <span>June 12, 2026</span>
              </div>
              <div className="flex items-center">
                <GlobeAltIcon className="h-4 w-4 mr-1" />
                <span>NYC, NY</span>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-white mb-4 leading-tight">
              SpeedR AI announced $2M Seed to Launch AI Doctor That Diagnoses
              Patients in Four Minutes
            </h2>

            <p className="text-xl text-primary-200 leading-relaxed">
              The startup's telehealth platform uses computer vision and AI to
              pre-diagnose patients and automatically book appointments, cutting
              clinician admin time by 40%.
            </p>
          </div>

          {/* Body Content */}
          <div className="space-y-6 text-primary-200 leading-relaxed">
            <p>
              SpeedR AI, a healthcare startup developing AI-driven telemedicine
              solutions, is raising $2 million in seed funding to expand its
              flagship product, AI Doctor, which can pre-diagnose patients in
              just four minutes via secure video.
            </p>

            <p>
              AI Doctor combines computer vision and artificial intelligence to
              evaluate patients, suggest potential diagnoses, and schedule
              appointments with the appropriate physician — all automatically.
              The platform reduces clinician administrative tasks by up to 40%,
              freeing doctors to focus on patient care.
            </p>

            <p>
              The seed round, led by PK Ramani "leading healthcare-focused Angel
              investor" underscores strong confidence in SpeedR AI's ability to
              scale AI-assisted telehealth solutions. The company has already
              secured a $15 million licensing LOI, highlighting early market
              validation.
            </p>

            <blockquote className="border-l-4 border-primary-500 pl-6 py-2 my-8 bg-dark-900/50 rounded-r-lg">
              <p className="text-white text-lg italic">
                "AI Doctor is transforming the first point of contact in
                healthcare," said Kris Christopher, Founder & CEO of SpeedR AI.
                "Our goal is to make healthcare faster, more accessible, and
                more efficient, while reducing the administrative burden on
                clinicians."
              </p>
            </blockquote>

            <p>
              The company plans to use the new funding to expand provider
              integrations, complete clinical validation, and accelerate
              adoption across telehealth networks.
            </p>
          </div>

          {/* About Section */}
          <div className="mt-12 pt-8 border-t border-primary-900/30">
            <h3 className="text-2xl font-bold text-white mb-4">
              About SpeedR AI:
            </h3>
            <p className="text-primary-200 leading-relaxed mb-6">
              SpeedR AI is on a mission to modernize telehealth. Its AI Doctor
              platform combines video-based assessments, automated triage, and
              appointment booking to deliver fast, accurate, and accessible
              care. Learn more at speedr.co.
            </p>
          </div>

          {/* Media Contact */}
          <div className="mt-12 pt-8 border-t border-primary-900/30">
            <h3 className="text-xl font-bold text-white mb-4">
              Media Contact:
            </h3>
            <div className="bg-dark-900/50 rounded-lg p-6 border border-primary-900/30">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="p-2 rounded-lg bg-primary-500/20">
                    <UserIcon className="h-6 w-6 text-primary-400" />
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white">
                    Kris Christopher
                  </h4>
                  <p className="text-primary-300">Founder & CEO, SpeedR AI</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <p className="text-primary-200">
                      <span className="font-medium">Email:</span> kris@speedr.co
                    </p>
                    <p className="text-primary-200">
                      <span className="font-medium">Website:</span>{" "}
                      https://speedr.co
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <motion.button
              className="px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Download Press Kit
            </motion.button>
            <motion.button
              className="px-6 py-3 bg-dark-900 text-primary-400 font-semibold rounded-lg border border-primary-900/30 hover:bg-dark-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Request Interview
            </motion.button>
          </div>
        </motion.div>

        {/* Additional Press Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-primary-900/20 text-center">
            <div className="text-3xl font-bold text-primary-500 mb-2">$2M</div>
            <div className="text-white font-semibold mb-1">Seed Funding</div>
            <div className="text-primary-200 text-sm">Led by PK Ramani</div>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-primary-900/20 text-center">
            <div className="text-3xl font-bold text-primary-500 mb-2">
              4 min
            </div>
            <div className="text-white font-semibold mb-1">Diagnosis Time</div>
            <div className="text-primary-200 text-sm">
              AI-powered assessment
            </div>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-primary-900/20 text-center">
            <div className="text-3xl font-bold text-primary-500 mb-2">40%</div>
            <div className="text-white font-semibold mb-1">
              Admin Time Reduced
            </div>
            <div className="text-primary-200 text-sm">For clinicians</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PressRelease;
